import React, { useEffect, useMemo, useState } from "react";
import Layout from "@/components/Layout";
import api from "@/lib/api";
import { CartesianGrid, Legend, Line, LineChart, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";

const COLORS = ["#0D9488", "#F97316", "#0284C7", "#EAB308", "#DB2777", "#7C3AED", "#16A34A", "#475569"];
const band = (p) => (p == null ? "bg-slate-100 text-slate-500" : p >= 90 ? "bg-emerald-100 text-emerald-800" : p >= 75 ? "bg-amber-100 text-amber-800" : "bg-rose-100 text-rose-800");

function Trend({ series, idx }) {
  return (
    <div className="flex items-end gap-1 h-8" data-testid={`quality-spark-${idx}`}>
      {series.map((s) => (
        <div key={s.month} title={`${s.month}: ${s.pct == null ? "no turnovers" : `${s.pct}% of ${s.total}`}`}
          className={`w-3 rounded-sm ${s.pct == null ? "bg-slate-200" : s.pct >= 90 ? "bg-emerald-500" : s.pct >= 75 ? "bg-amber-400" : "bg-rose-500"}`}
          style={{ height: `${s.pct == null ? 8 : Math.max(10, s.pct)}%` }} />
      ))}
    </div>
  );
}

export default function CleanerQuality() {
  const [months, setMonths] = useState(6);
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/team/quality-trend", { params: { months } }).then((r) => setD(r.data)).catch(() => setD({ months: [], cleaners: [] })); }, [months]);
  const chart = useMemo(() => (d ? d.months.map((m) => {
    const row = { month: m.slice(2) };
    d.cleaners.forEach((c) => { row[c.name] = c.series.find((s) => s.month === m)?.pct ?? null; });
    return row;
  }) : []), [d]);
  return (
    <Layout title="Cleaner quality" subtitle="Share of turnovers with no AI-flagged rooms or confirmed damage, month by month.">
      <div className="flex gap-2 mb-6" data-testid="quality-months">
        {[3, 6, 12].map((m) => (
          <button key={m} onClick={() => setMonths(m)} data-testid={`quality-months-${m}`}
            className={`px-4 py-1.5 rounded-full text-sm border transition-colors ${months === m ? "bg-slate-900 text-white border-slate-900" : "border-slate-200 hover:bg-slate-50"}`}>{m} months</button>
        ))}
      </div>
      {!d ? <div className="text-sm text-slate-400">Loading…</div> : d.cleaners.length === 0 ? (
        <div className="text-sm text-slate-400 italic" data-testid="quality-empty">No completed turnovers in this period yet.</div>
      ) : (
        <div className="space-y-6">
          <div className="ns-card p-6" data-testid="quality-chart">
            <div className="font-serif text-xl mb-4">Monthly quality score</div>
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={chart}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                  <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                  <YAxis domain={[0, 100]} tick={{ fontSize: 11 }} width={34} unit="%" />
                  <Tooltip formatter={(v) => (v == null ? "—" : `${v}%`)} />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <ReferenceLine y={90} stroke="#16A34A" strokeDasharray="4 4" />
                  <ReferenceLine y={75} stroke="#EAB308" strokeDasharray="4 4" />
                  {d.cleaners.map((c, i) => (
                    <Line key={c.id} type="monotone" dataKey={c.name} stroke={COLORS[i % COLORS.length]} strokeWidth={2.5} dot={{ r: 3 }} connectNulls />
                  ))}
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
          <div className="ns-card overflow-hidden" data-testid="quality-table">
            <table className="w-full text-sm">
              <thead><tr className="text-left">
                <th className="ns-label px-6 py-3">Cleaner</th><th className="ns-label px-6 py-3">Score</th>
                <th className="ns-label px-6 py-3">Turnovers</th><th className="ns-label px-6 py-3">Trend</th>
              </tr></thead>
              <tbody>
                {d.cleaners.map((c, i) => (
                  <tr key={c.id} className="border-t border-slate-100" data-testid={`quality-row-${c.id}`}>
                    <td className="px-6 py-3 font-medium"><span className="inline-block w-2.5 h-2.5 rounded-full mr-2" style={{ background: COLORS[i % COLORS.length] }} />{c.name}</td>
                    <td className="px-6 py-3"><span className={`px-2.5 py-1 rounded-full text-xs font-medium ${band(c.pct)}`} data-testid={`quality-score-${c.id}`}>{c.pct}%</span></td>
                    <td className="px-6 py-3 text-slate-600">{c.flag_free} / {c.total} flag-free</td>
                    <td className="px-6 py-3"><Trend series={c.series} idx={i} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </Layout>
  );
}
